import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";
import { FlaskConical, Search, RefreshCw } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import Modal from "Common/Components/Modal";
import useLabTestOrders from "hooks/useLabTestOrders";
import {
  LabTestOrder,
  updateLabTestOrderStatus,
} from "services/labTestOrder.service";

const STATUS_COLORS: Record<string, string> = {
  Placed: "bg-blue-100 text-blue-800",
  Processing: "bg-yellow-100 text-yellow-800",
  Completed: "bg-green-100 text-green-800",
  Cancelled: "bg-red-100 text-red-800",
};

const PAYMENT_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  successful: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

const NEXT_STATUSES: Record<string, string[]> = {
  Placed: ["Processing", "Cancelled"],
  Processing: ["Completed", "Cancelled"],
  Completed: [],
  Cancelled: [],
};

const LabTestOrders = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<LabTestOrder | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [showModal, setShowModal] = useState(false);

  const { data: response, isLoading, isError, refetch } = useLabTestOrders({
    page,
    limit: 20,
    status: statusFilter || undefined,
  });

  const orders = response?.data?.data ?? [];

  // Followed here from a lab-order alert. This list has no per-record route, so the id
  // arrives as `?focus=` and rings the row it names.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    orders,
    isLoading
  );
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const { isLoading: saving, mutate: doUpdate } = useMutation(
    () => updateLabTestOrderStatus(selectedOrder!._id, newStatus),
    {
      onSuccess() {
        toast.success("Status updated");
        queryClient.invalidateQueries("lab-test-orders");
        setShowModal(false);
        setSelectedOrder(null);
        setNewStatus("");
      },
      onError() {
        toast.error("Failed to update status");
      },
    }
  );

  const openStatusModal = (order: LabTestOrder) => {
    setSelectedOrder(order);
    setNewStatus("");
    setShowModal(true);
  };

  const filteredOrders = search
    ? orders.filter(
        (o) =>
          o.orderRef.toLowerCase().includes(search.toLowerCase()) ||
          o.user?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
          o.user?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
          o.user?.email?.toLowerCase().includes(search.toLowerCase())
      )
    : orders;

  return (
    <React.Fragment>
      <BreadCrumb title="Lab Test Orders" pageTitle="Lab" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by ref, name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:w-44"
          >
            <option value="">All Statuses</option>
            <option value="Placed">Placed</option>
            <option value="Processing">Processing</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Order Ref</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Lab</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Appointment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={8} label="Couldn't load lab test orders" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FlaskConical className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No lab test orders found</p>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order._id}
                    ref={scrollRowIntoView(isFocused(order._id))}
                    className={`hover:bg-gray-50 transition-colors ${focusRowClass(
                      isFocused(order._id)
                    )}`}
                  >
                    <td data-label="Order Ref" data-cell="title" className="px-4 py-3 font-mono text-xs text-gray-700">{order.orderRef}</td>
                    <td data-label="Patient" data-cell="block" className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {order.user?.firstName} {order.user?.lastName}
                      </p>
                      <p className="text-xs text-gray-400">{order.user?.email}</p>
                    </td>
                    <td data-label="Lab" className="px-4 py-3 text-gray-600">{order.lab?.name ?? "—"}</td>
                    <td data-label="Service" className="px-4 py-3 text-gray-600">{order.labService?.description ?? "—"}</td>
                    <td data-label="Appointment" className="px-4 py-3 text-gray-600">
                      {order.appointmentDate
                        ? new Date(order.appointmentDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td data-label="Payment" className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_COLORS[order.paymentStatus] ?? "bg-gray-100 text-gray-600"}`}>
                        {order.paymentStatus}
                      </span>
                    </td>
                    <td data-label="Status" className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] ?? "bg-gray-100 text-gray-600"}`}>
                        {order.status}
                      </span>
                    </td>
                    <td data-label="Actions" data-cell="actions" className="px-4 py-3">
                      {(NEXT_STATUSES[order.status]?.length ?? 0) > 0 && (
                        <button
                          onClick={() => openStatusModal(order)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                          Update
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        id="labOrderModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">
            Update Order Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-5 py-4 flex flex-col gap-4">
          {selectedOrder && (
            <>
              <div className="text-sm text-gray-600">
                Order <span className="font-mono font-medium">{selectedOrder.orderRef}</span>
                {" "}· Current:{" "}
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedOrder.status] ?? "bg-gray-100 text-gray-600"}`}>
                  {selectedOrder.status}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select status...</option>
                  {NEXT_STATUSES[selectedOrder.status]?.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!newStatus || saving}
                  onClick={() => doUpdate()}
                  className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                >
                  {saving ? "Saving..." : "Update Status"}
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default LabTestOrders;
